import { Button, EButtonColor } from '@web_opxp/components';
import { AuthSelectors, DateUtils, FirebaseSelectors, StatsSelectors, useTranslation } from '@web_opxp/features';
import * as React from 'react';
import { connect } from 'react-redux';
import { SELF_ASSESSMENT_STAT_ID } from '../base/constants';
import LevelSection from '../components/LevelSection/LevelSection';
import { CyclesSelectors, cycleHasCustomIntensity, getTrainingConfig } from '../cycles';
import { EvaluationSelectors } from '../evaluation';
import { IState } from '../store/state';
import ConfirmaSkipAssessmentDialog from './ConfirmSkipAssessmentDialog';
import './CycleCompletedSection.scss';
import { DashboardActions } from './slice';
import { getQuestionaireConfigForCycle, startNextAssessmentPossible } from './utils';

export interface ICycleCompletedSectionProps {
  canStartAssessment: boolean;
  cfq: boolean;
  cycleId: number;
  onSkipAssessment: (addSession?: boolean) => void;
  onStartAssessment: (cfq: boolean) => void;
}

export const CycleCompletedSectionFC: React.FC<ICycleCompletedSectionProps> = ({
  canStartAssessment,
  cfq,
  cycleId,
  onSkipAssessment,
  onStartAssessment,
}) => {
  const [loading, toggleLoading] = React.useState(false);
  const [currentCycleId, setCurrentCycleId] = React.useState<number | undefined>(undefined);
  const [skipConfimVisible, setSkipConfimVisible] = React.useState(false);
  const [addSession, setAddSession] = React.useState<boolean | undefined>(undefined);
  const { t } = useTranslation();

  let translatedSectionTitle: string;
  let assessmentButtonLabel: string;
  let sectionSubtitle: string;
  const alternateAssessmentOptions = cycleId > 1 && canStartAssessment;
  let redoCycleButtonLabel: string;

  if (cfq) {
    translatedSectionTitle = t('Dashboard.TitleCongratesMiniTraining');
    assessmentButtonLabel = t('Dashboard.CTA.StartTestMiniTraining');
    sectionSubtitle = t('Dashboard.CongratesMessageMiniTraining');
    redoCycleButtonLabel = t('Dashboard.CTA.RepeatSamePlanMiniTraining');
  } else {
    translatedSectionTitle = t('Dashboard.TitleCongratesLevel', { cycleId: currentCycleId ?? cycleId });
    assessmentButtonLabel = t('Dashboard.CTA.StartTest');
    sectionSubtitle = t('Dashboard.CongratesMessage');
    redoCycleButtonLabel = t('Dashboard.CTA.RepeatSamePlan');
  }
  if (!canStartAssessment) {
    sectionSubtitle = t('Dashboard.CongratesMessageNewPlan');
  }
  if (alternateAssessmentOptions) {
    redoCycleButtonLabel = t('Dashboard.CTA.AskLaterCycle');
  }

  const redoSessionButtonLabel = alternateAssessmentOptions ? t('Dashboard.CTA.AskLaterSession') : '';

  const handleConfirmSkip = () => {
    // Hide the dialog
    setSkipConfimVisible(false);
    setCurrentCycleId(cycleId);
    toggleLoading(true);
    onSkipAssessment(addSession);
    setAddSession(undefined);
  };

  const handleCancelSkip = () => {
    // Hide the dialog
    setSkipConfimVisible(false);
    setAddSession(undefined);
  };

  const handleSkipAssessment = (addSession?: boolean) => {
    // Save the current cycle ID to avoid the brief display of next cycle ID when repeating the session
    setAddSession(addSession);
    // Show the dialog
    setSkipConfimVisible(true);
  };
  const handleStartAssessment = () => {
    setCurrentCycleId(cycleId);
    toggleLoading(true);
    onStartAssessment(cfq);
  };

  const sectionClassName = `cycle-completed${alternateAssessmentOptions ? ' alternate' : ''}`;
  const skipAssessmentButton = alternateAssessmentOptions ? (
    <button
      className="cycle-completed-section-button skip-link"
      onClick={() => handleSkipAssessment(true)}
      disabled={loading}>
      <span>{redoSessionButtonLabel}</span>
    </button>
  ) : (
    <Button
      disabled={loading}
      color={EButtonColor.white}
      label={redoCycleButtonLabel}
      onClick={() => handleSkipAssessment()}
      className="cycle-completed-section-button"
    />
  );
  const skipAssessmentOptionButton = alternateAssessmentOptions ? (
    <button
      className="cycle-completed-section-button skip-link"
      onClick={() => handleSkipAssessment()}
      disabled={loading}>
      <span>{redoCycleButtonLabel}</span>
    </button>
  ) : null;
  return (
    <LevelSection title={translatedSectionTitle} subtitle={sectionSubtitle} className={sectionClassName}>
      {skipAssessmentOptionButton}
      {skipAssessmentButton}
      <Button
        disabled={loading || !canStartAssessment}
        color={EButtonColor.white}
        label={assessmentButtonLabel}
        onClick={handleStartAssessment}
        className="cycle-completed-section-button"
      />
      <ConfirmaSkipAssessmentDialog
        show={skipConfimVisible}
        onConfirm={handleConfirmSkip}
        onCancel={handleCancelSkip}
      />
    </LevelSection>
  );
};

export const mapStateToProps = (state: IState) => {
  const firebaseLoaded = FirebaseSelectors.isReady(state);
  const cyclesLoaded = !CyclesSelectors.isCyclesLoading(state);
  const statsLoaded = !StatsSelectors.isStatsLoading(state);
  let isMiniTraining = false;
  let canStartAssessment = false;
  let cfqPending = false;
  let cycleId = 0;
  if (firebaseLoaded && cyclesLoaded && statsLoaded) {
    // detect mini training completed
    cycleId = CyclesSelectors.getCurrentCycleId(state);
    const lastAssessmentTimestamp = EvaluationSelectors.getLastAssessmentEvaluationTimestamp(state);
    isMiniTraining = cycleId === 1 || cycleHasCustomIntensity(cycleId);
    // for mini training it is always allowed to start assessment
    // even on the first day
    // always can start assessment after mini training
    canStartAssessment = true;
    if (!isMiniTraining) {
      // detect if assessment ready to be started
      const trainingConfig = getTrainingConfig();
      const assessmentRepeatConfig = trainingConfig?.assessment_repeat_config;
      const userCreated = AuthSelectors.getCreated(state);
      canStartAssessment = startNextAssessmentPossible(
        userCreated ?? 0,
        lastAssessmentTimestamp,
        assessmentRepeatConfig
      );
      // detect if CFQ self assessment questionaire should be started
      const questionaireRepeatConfig = getQuestionaireConfigForCycle(cycleId);
      if (questionaireRepeatConfig?.enabled) {
        const questionareRepeatDays = 60 * 60 * 24 * questionaireRepeatConfig.repeat_days;
        const converted = Number(StatsSelectors.getStatStringValueById(state, SELF_ASSESSMENT_STAT_ID));
        const lastQuestionaireDone = isNaN(converted) ? 0 : converted;
        const now = DateUtils.getUnixTimestamp();
        cfqPending = now - lastQuestionaireDone >= questionareRepeatDays;
      }
    }
  }
  const cfq = cfqPending || isMiniTraining;
  return {
    cycleId,
    canStartAssessment,
    cfq,
  };
};

export const mapDispatchToProps = {
  onStartAssessment: DashboardActions.startAssessment,
  onSkipAssessment: DashboardActions.skipAssessment,
};

const CycleCompletedSection = connect(mapStateToProps, mapDispatchToProps)(CycleCompletedSectionFC);
CycleCompletedSection.displayName = 'CycleCompletedSection';

export default CycleCompletedSection;
